import React, { useState, useContext, useEffect, useRef } from "react";
import { ApiContext } from "../../../../context/MyContext";
import { updatePricingPlan } from "../../../../service/pricingPlanService";
import { getSubscriptionCommonUtils } from "../../../../service/SubscriptionWellDhanService";
import { Toaster } from "../../../../common/Toaster/Toaster";

const EditPricingPlanModal = ({ pricing, onClose, onSuccess }) => {
  const { post, get } = useContext(ApiContext);

  const [form, setForm] = useState({
    duration: "",
    price: "",
    max_discount: "",
    country: "",
    currency: "",
  });

  const [loading, setLoading] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);
  const [toastType, setToastType] = useState("success");
  const [commonUtils, setCommonUtils] = useState(null);
  const [loadingUtils, setLoadingUtils] = useState(true);

  //! Fetch utils (country & currency master)
  useEffect(() => {
    const fetchUtils = async () => {
      try {
        const result = await getSubscriptionCommonUtils(get);
        if (result.success) {
          setCommonUtils(result.data);
        } else {
          setToastMessage(
            result.message || "Failed to load configuration data"
          );
          setToastType("error");
        }
      } catch (err) {
        setToastMessage("Failed to load configuration data");
        setToastType("error");
      } finally {
        setLoadingUtils(false);
      }
    };
    fetchUtils();
  }, [get]);

  // Set initial edit values
  useEffect(() => {
    if (pricing && commonUtils) {
      // Convert currency symbol to name for dropdown
      let currencyName = pricing?.currency ?? "Indian Rupee";
      if (pricing?.currency && commonUtils?.denominations) {
        // If currency is a symbol, find the corresponding name
        const denomination = commonUtils.denominations.find(
          (d) =>
            d.symbol?.toLowerCase() === pricing.currency.toLowerCase() ||
            d.name?.toLowerCase() === pricing.currency.toLowerCase()
        );
        if (denomination?.name) {
          currencyName = denomination.name;
        }
      }

      // Convert country name - ensure it matches the options
      let countryName = pricing?.country ?? "India";
      if (pricing?.country && commonUtils?.countries) {
        const country = commonUtils.countries.find(
          (c) => c.name?.toLowerCase() === pricing.country.toLowerCase()
        );
        if (country?.name) {
          countryName = country.name;
        }
      }

      setForm({
        duration: pricing?.duration ?? pricing?.pricingDuration ?? "",
        price: pricing?.price ?? "",
        max_discount: pricing?.maxDiscount ?? "",
        country: countryName,
        currency: currencyName,
      });
    }
  }, [pricing, commonUtils]);

  // Auto hide toast
  useEffect(() => {
    if (toastMessage) {
      const timeout = setTimeout(() => setToastMessage(null), 4000);
      return () => clearTimeout(timeout);
    }
  }, [toastMessage]);

  const handleChange = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  // ✅ Get country_id by NAME from utils
  const getCountryId = (countryName) => {
    const found = commonUtils?.countries?.find(
      (c) => c.name?.toLowerCase() === countryName.toLowerCase()
    );
    return found?.id || null;
  };

  // ✅ Get denomination_id by NAME or SYMBOL from utils
  const getDenominationId = (currencyValue) => {
    if (!currencyValue || !commonUtils?.denominations) return null;
    const found = commonUtils.denominations.find(
      (d) =>
        d.name?.toLowerCase() === currencyValue.toLowerCase() ||
        d.symbol?.toLowerCase() === currencyValue.toLowerCase()
    );
    return found?.id || null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setToastMessage(null);

    try {
      const countryId = getCountryId(form.country);
      const denominationId = getDenominationId(form.currency);

      if (!countryId) throw new Error(`Invalid country: ${form.country}`);
      if (!denominationId)
        throw new Error(`Invalid currency: ${form.currency}`);

      const payload = {
        event_type_id: "4850387a-8838-45d5-8c65-dce9d1f86de6",
        plan_pricings: [
          {
            operation: "UPDATE",
            id: pricing.planPricingId,
            plan_id: pricing.planId,
            plan_duration_days: Number(form.duration) * 30,
            price: Number(form.price),
            max_discount_percent: Number(form.max_discount),
            country_id: countryId,
            denomination_id: denominationId,
          },
        ],
      };

      await updatePricingPlan(post, pricing.planPricingId, payload);

      setToastMessage("Pricing updated successfully!");
      setToastType("success");

      setTimeout(() => onSuccess && onSuccess(), 1200);
    } catch (err) {
      console.error(err);
      setToastMessage(err.message || "Failed to update pricing.");
      setToastType("error");
    } finally {
      setLoading(false);
    }
  };

  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && !loading) {
        onClose();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose, loading]);

  // Focus management - focus the first input on mount
  const durationInputRef = useRef(null);
  useEffect(() => {
    if (durationInputRef.current && !loadingUtils) {
      durationInputRef.current.focus();
    }
  }, [loadingUtils]);

  // Get available countries and currencies from commonUtils
  const countryOptions = commonUtils?.countries?.map((c) => c.name) || [];
  const currencyOptions = commonUtils?.denominations?.map((d) => d.name) || [];

  if (loadingUtils) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white/70">
        <div className="text-gray-600">Loading configuration...</div>
      </div>
    );
  }

  return (
    <div 
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/20"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-pricing-modal-title"
    >
      <div 
        className="bg-white rounded-xl shadow-lg p-6 min-w-[350px] relative max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button 
          className="absolute top-2 right-3 text-2xl font-bold text-gray-500 hover:text-gray-700 transition-colors" 
          onClick={onClose}
          disabled={loading}
          aria-label="Close modal"
        >
          ×
        </button>

        <h3 id="edit-pricing-modal-title" className="text-lg font-bold mb-4">Edit Pricing Option</h3>

        <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
          <div>
            <label htmlFor="edit-duration-input" className="block font-semibold mb-1">
              Duration (months)
            </label>
            <input
              id="edit-duration-input"
              ref={durationInputRef}
              type="number"
              min={1}
              className="border px-2 py-1 rounded w-full focus:outline-none focus:ring-2 focus:ring-orange-primary"
              value={form.duration}
              onChange={(e) => handleChange("duration", e.target.value)}
              required
              disabled={loading}
            />
          </div>

          <div>
            <label className="block font-semibold mb-1">Price</label>
            <input
              type="number"
              min={0}
              className="border px-2 py-1 rounded w-full"
              value={form.price}
              onChange={(e) => handleChange("price", e.target.value)}
              required
            />
          </div>

          <div>
            <label className="block font-semibold mb-1">Max Discount (%)</label>
            <input
              type="number"
              min={0}
              max={100}
              className="border px-2 py-1 rounded w-full"
              value={form.max_discount}
              onChange={(e) => handleChange("max_discount", e.target.value)}
              required
            />
          </div>

          <div>
            <label className="block font-semibold mb-1">Country</label>
            <select
              className="border px-2 py-1 rounded w-full"
              value={form.country}
              onChange={(e) => handleChange("country", e.target.value)}
              required
            >
              <option value="">Select a country</option>
              {countryOptions.map((country) => (
                <option key={country} value={country}>
                  {country}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block font-semibold mb-1">Currency</label>
            <select
              className="border px-2 py-1 rounded w-full"
              value={form.currency}
              onChange={(e) => handleChange("currency", e.target.value)}
              required
            >
              <option value="">Select a currency</option>
              {currencyOptions.map((currency) => (
                <option key={currency} value={currency}>
                  {currency}
                </option>
              ))}
            </select>
          </div>

          <div className="flex gap-3 mt-2">
            <button
              type="submit"
              disabled={loading}
              className="bg-orange-primary text-white font-semibold px-5 py-2 rounded shadow hover:bg-orange-700 disabled:opacity-50 transition-colors"
              aria-label={loading ? "Saving pricing..." : "Save pricing"}
            >
              {loading ? "Saving..." : "Save"}
            </button>

            <button
              type="button"
              onClick={onClose}
              className="bg-gray-200 text-gray-700 px-5 py-2 rounded shadow hover:bg-gray-300 disabled:opacity-50"
              disabled={loading}
            >
              Cancel
            </button>
          </div>

          {toastMessage && <Toaster message={toastMessage} type={toastType} />}
        </form>
      </div>
    </div>
  );
};

export default EditPricingPlanModal;
